import React, { useState } from 'react';
import {
  Ban,
  CalendarClock,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Send,
  X,
} from 'lucide-react';
import DateSelector from '../analytics/DateSelector';
import { reportDeliveryIntelligenceSignal } from './deliveryIntelligenceApi';

const SIGNAL_OPTIONS = [
  { id: 'recipient_confirmation', label: 'Cliente confirmó', icon: CheckCircle2 },
  { id: 'recipient_response', label: 'Cliente respondió', icon: MessageCircle },
  { id: 'cancellation_request', label: 'Solicitar cancelación', icon: Ban },
  { id: 'postponement_request', label: 'Solicitar postergación', icon: CalendarClock },
];

const CHANNELS = [
  ['store_whatsapp', 'WhatsApp de la tienda'],
  ['phone', 'Llamada telefónica'],
  ['other', 'Otro canal'],
];

const CANCELLATION_REASONS = [
  ['customer_requested', 'Cliente solicitó cancelación'],
  ['no_response', 'No responde'],
  ['store_requested', 'Tienda solicita cancelación'],
  ['duplicate_order', 'Pedido duplicado'],
  ['invalid_address', 'Dirección inválida'],
  ['other', 'Otro'],
];

function requestMessage(error) {
  return error?.response?.data?.message || 'No se pudo enviar la señal. Intenta nuevamente.';
}

export default function StoreDeliverySignalModal({ item, onClose, onCompleted }) {
  const [signalType, setSignalType] = useState('recipient_confirmation');
  const [channel, setChannel] = useState('store_whatsapp');
  const [outcome, setOutcome] = useState('negative_response');
  const [reasonCode, setReasonCode] = useState('customer_requested');
  const [requestedDate, setRequestedDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    if (signalType === 'postponement_request' && !requestedDate) {
      setError('Selecciona la fecha solicitada por el cliente.');
      return;
    }
    if (signalType === 'postponement_request' && !note.trim()) {
      setError('Indica el motivo de la postergación solicitada.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        signal_type: signalType,
        note: note.trim() || null,
      };
      if (signalType === 'recipient_confirmation' || signalType === 'recipient_response') {
        payload.channel = channel;
      }
      if (signalType === 'recipient_response') payload.outcome = outcome;
      if (signalType === 'cancellation_request') {
        payload.reason_code = reasonCode;
        payload.channel = channel;
      }
      if (signalType === 'postponement_request') {
        payload.requested_date = requestedDate;
        payload.channel = channel;
        payload.reason_code = 'store_requested';
      }
      const result = await reportDeliveryIntelligenceSignal(item.delivery_point_id, payload);
      await onCompleted?.(result);
    } catch (requestError) {
      setError(requestMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Reportar novedad a Fast Max">
      <div className="max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#B1336E] font-montserrat">Avisar a operaciones</p>
            <h2 className="mt-1 text-xl font-extrabold text-slate-900 font-montserrat">Entrega #{item.delivery_point_id}</h2>
            <p className="mt-1 text-xs text-slate-500 font-montserrat">Fast Max revisará tu reporte antes de ejecutar cambios.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5 px-4 py-5 sm:px-6">
          <div>
            <label className="mb-2 block text-sm font-extrabold text-slate-800 font-montserrat">¿Qué deseas reportar?</label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SIGNAL_OPTIONS.map(option => {
                const Icon = option.icon;
                const active = signalType === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => { setSignalType(option.id); setError(''); }}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm font-bold transition-colors font-montserrat ${active ? 'border-[#B1336E] bg-[#B1336E]/5 text-[#8f2859]' : 'border-slate-200 text-slate-700 hover:border-[#B1336E]/30'}`}
                  >
                    <Icon className="h-5 w-5 shrink-0" /> {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {signalType === 'recipient_response' && (
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-800 font-montserrat">Resultado de la respuesta</label>
              <select value={outcome} onChange={event => setOutcome(event.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/15 font-montserrat">
                <option value="negative_response">Respuesta negativa o con observaciones</option>
                <option value="unclear">Respuesta poco clara / requiere revisión</option>
              </select>
            </div>
          )}

          {signalType === 'cancellation_request' && (
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-800 font-montserrat">Motivo de la solicitud</label>
              <select value={reasonCode} onChange={event => setReasonCode(event.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/15 font-montserrat">
                {CANCELLATION_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          )}

          {signalType === 'postponement_request' && (
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-800 font-montserrat">Nueva fecha solicitada</label>
              <DateSelector
                selectedDate={requestedDate}
                onDateChange={setRequestedDate}
                showCalendar={showCalendar}
                onToggleCalendar={() => setShowCalendar(value => !value)}
                label="Fecha solicitada"
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-bold text-slate-800 font-montserrat">Canal donde recibiste la información</label>
            <select value={channel} onChange={event => setChannel(event.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/15 font-montserrat">
              {CHANNELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-slate-800 font-montserrat">
              {signalType === 'postponement_request' ? 'Motivo de la postergación' : 'Contexto'}
              {signalType !== 'postponement_request' && <span className="font-normal text-slate-400"> (opcional)</span>}
            </label>
            <textarea value={note} onChange={event => setNote(event.target.value)} maxLength={500} rows={3} placeholder="Agrega solo el contexto necesario, sin datos sensibles adicionales." className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/15 font-montserrat" />
            <p className="mt-1 text-right text-[11px] text-slate-400 font-montserrat">{note.length}/500</p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900 font-montserrat">
            Este reporte crea una señal auditada. No cancela ni posterga la entrega automáticamente; un administrador conserva el control.
          </div>
          {error && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700 font-montserrat">{error}</p>}

          <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} disabled={submitting} className="rounded-xl bg-slate-100 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-200 disabled:opacity-60 font-montserrat">Volver</button>
            <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#B1336E] px-5 py-3 text-sm font-bold text-white hover:bg-[#9c2d61] disabled:opacity-60 font-montserrat">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? 'Enviando...' : 'Enviar a Fast Max'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
